import type {JobQueue, JobQueueLane, JobStatus} from './types';

/*
 * Where a job waits while Studio is too busy to start its next step, for the queue card on
 * the storyboard and videos pages and the chip on the library's cards. Newer APIs say it in
 * `job.queue`; older ones only in the stage ("Waiting for a free render slot (2 ahead, about
 * 12 min)"), which is read the same way.
 */

export interface QueueInfo {
  /** What it waits for; null when the server does not say. */
  lane: JobQueueLane | null;
  /** How many jobs are ahead of it; null when it cannot be told. */
  ahead: number | null;
  /** About how long until it starts, in seconds; null when it cannot be told. */
  etaSeconds: number | null;
  /** When the wait was estimated (ms since the epoch); null on older servers. */
  etaAt: number | null;
  /** When it started waiting (ms since the epoch); null when it cannot be told. */
  since: number | null;
  /**
   * Why it waits, as the server's short code ("starting", "backlog", "light-slots",
   * "capture-slots", "document-reader-busy", "provider-rate-limit", "provider-capacity"…).
   * Never shown as it is.
   */
  reason: string | null;
  /** The job's storyboard is being drafted, rather than its video made. */
  drafting: boolean;
}

interface Queued {
  status?: JobStatus | string;
  stage?: string | null;
  stageStartedAt?: string | null;
  queue?: JobQueue | null;
}

const LANES = new Set<string>(['plan', 'document', 'export', 'provider']);
const DRAFTING = new Set<string>(['ingesting', 'planning', 'checking']);
const WORKING = new Set<string>([
  'ingesting',
  'planning',
  'checking',
  'approved',
  'rendering',
]);

/*
 * The stages of a job waiting in a queue, for servers without the queue field: "Waiting to
 * start" (": Studio is busy, about 4 min"), "Waiting for a free render slot (2 ahead, about
 * 12 min)", "Waiting for a free Studio slot (next, about 1 min)", "Waiting for the document
 * reader", and "Studio's AI service is at capacity right now. Your video will continue
 * automatically."
 */
const WAITING = /^(?:Waiting\b|Studio['’]s AI service is at capacity\b)/i;

const count = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.round(value)
    : null;

const time = (value: unknown): number | null => {
  if (typeof value !== 'string' || !value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/** What a waiting stage waits for, from its words. */
function laneOfStage(stage: string, drafting: boolean): JobQueueLane | null {
  if (/\bdocument reader\b/i.test(stage)) return 'document';
  if (/\bAI service\b|\bat capacity\b/i.test(stage)) return 'provider';
  if (/\brender slot\b|\bexport/i.test(stage)) return 'export';
  if (/\bStudio slot\b/i.test(stage)) return 'plan';
  if (/^Waiting to start\b/i.test(stage) && drafting) return 'plan';
  return null;
}

/** "(2 ahead, about 12 min)", "(next, about 3 min)", ": Studio is busy, about 2 h". */
function readStage(stage: string) {
  const ahead = /\((?:[^)]*?\b)?(\d+) ahead\b/i.exec(stage);
  const next = /\(next\b/i.test(stage);
  const about = /\babout (\d+) ?(min|h)\b/i.exec(stage);
  return {
    ahead: ahead ? Number(ahead[1]) : next ? 0 : null,
    etaSeconds: about
      ? Number(about[1]) * (about[2].toLowerCase() === 'h' ? 3600 : 60)
      : null,
  };
}

/**
 * Where the job waits, or null while it does not. A server that sends `queue` is taken at its
 * word; without it, a waiting stage (see WAITING) says the job waits.
 */
export function queueOf(job: Queued | null | undefined): QueueInfo | null {
  if (!job) return null;
  const drafting = !!job.status && DRAFTING.has(job.status);
  if (job.queue !== undefined) {
    const queue = job.queue;
    if (!queue || queue.state !== 'waiting') return null;
    const reason = typeof queue.reason === 'string' ? queue.reason.trim() : '';
    return {
      lane:
        typeof queue.lane === 'string' && LANES.has(queue.lane)
          ? (queue.lane as JobQueueLane)
          : null,
      ahead: count(queue.ahead),
      etaSeconds: count(queue.etaSeconds),
      etaAt: time(queue.etaAt),
      since: time(queue.since) ?? time(job.stageStartedAt),
      reason: reason || null,
      drafting,
    };
  }
  // A stage left over from work that stopped (a library row of a failed job) waits for nothing.
  if (!job.status || !WORKING.has(job.status)) return null;
  const stage = job.stage?.trim() || '';
  if (!WAITING.test(stage)) return null;
  return {
    lane: laneOfStage(stage, drafting),
    ...readStage(stage),
    etaAt: null,
    since: time(job.stageStartedAt),
    reason: null,
    drafting,
  };
}

/** True while the job waits in one of Studio's queues. */
export const isQueued = (job: Queued | null | undefined) => !!queueOf(job);

/**
 * A wait in whole, rounded units: under a minute; minutes up to an hour; then hours and five
 * minutes; whole hours from two hours.
 */
export function roundWait(
  seconds: number,
): {minutes: number; hours: number} | 'underMinute' {
  if (seconds < 60) return 'underMinute';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return {hours: 0, minutes};
  if (minutes >= 120) return {hours: Math.round(minutes / 60), minutes: 0};
  const fives = Math.round((minutes - 60) / 5) * 5;
  return fives >= 60 ? {hours: 2, minutes: 0} : {hours: 1, minutes: fives};
}

/*
 * Why a job failed, when the reason passes: the job's errorCode says so ("provider-capacity",
 * "studio-busy"). Servers without it are read from the sentence: "Studio's AI service is at
 * capacity right now. …" or "Studio was busy and stopped this video. …". Generating
 * continues from where it was.
 */
const PROVIDER_CAPACITY =
  /\[provider-capacity\]|\bAI service is at capacity\b/i;
const TRANSIENT = /\[studio-busy\]|^\s*Studio was busy\b/i;

export type PassingFailure = 'provider-capacity' | 'transient';

/** Whether a job's failure passes on its own, and why; null for any other failure. */
export function passingFailure(
  error: string | null | undefined,
  code?: string | null,
): PassingFailure | null {
  if (code === 'provider-capacity') return 'provider-capacity';
  if (code === 'studio-busy') return 'transient';
  const text = error || '';
  if (PROVIDER_CAPACITY.test(text)) return 'provider-capacity';
  if (TRANSIENT.test(text)) return 'transient';
  return null;
}

/** A failure's text without the bracketed codes older servers added to it ("[provider-capacity]"). */
export const withoutCodes = (text: string) =>
  text.replace(/\s*\[[a-z][a-z0-9-]*\]\s*/g, ' ').trim();
